import React, { useState, useCallback, useRef } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Box,
    Chip,
    LinearProgress,
    IconButton,
    List,
} from "@mui/material";
import { styled, keyframes } from "@mui/material/styles";
import CustomButton from "../components/common/CustomButton";
import {
    Upload,
    FileText,
    X,
    CheckCircle,
    AlertTriangle,
    CloudUpload,
    Briefcase,
    FileUser,
    ArrowLeft,
} from "lucide-react";

// ─────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────
const QUOTES = [
    "Great things take time. Hang tight!",
    "Uploading your success...",
    "Every byte counts, uploading now!",
    "Transforming effort into opportunity...",
    "Hold tight! Magic is happening...",
];

// Default constants for JD and Resume
const MAX_FILE_SIZE_MB = 5;
const ACCEPTED_TYPES = ".pdf,.doc,.docx";
const ALLOWED_EXTS = ["pdf", "doc", "docx"];
const FORMAT_LABELS = ["PDF", "DOC", "DOCX"];

// Logo-specific constants
const LOGO_MAX_FILE_SIZE_MB = 2;
const LOGO_ACCEPTED_TYPES = ".jpg,.jpeg,.png,.img,.pdf";
const LOGO_ALLOWED_EXTS = ["jpg", "jpeg", "png", "img", "pdf"];
const LOGO_FORMAT_LABELS = ["JPG", "PNG", "IMG", "PDF"];

// Helper to get size limit based on upload type
const getMaxFileSize = (uploadType) => uploadType === "logo" ? LOGO_MAX_FILE_SIZE_MB : MAX_FILE_SIZE_MB;
const getAcceptedTypes = (uploadType) => uploadType === "logo" ? LOGO_ACCEPTED_TYPES : ACCEPTED_TYPES;
const getAllowedExts = (uploadType) => uploadType === "logo" ? LOGO_ALLOWED_EXTS : ALLOWED_EXTS;
const getFormatLabels = (uploadType) => uploadType === "logo" ? LOGO_FORMAT_LABELS : FORMAT_LABELS;

// ─────────────────────────────────────────────
// Upload type config — drives ALL text/icons
// ─────────────────────────────────────────────
const TYPE_CONFIG = {
    jd: {
        chipLabel: "Job Description",
        title: "Upload Job Description",
        dropPrompt: "Drop your JD here",
        successLabel: "JD",
        accentBg: "#eff6ff",
        accentColor: "#2563eb",
        iconBg: "#dbeafe",
        gradientFrom: "#2563eb",
        gradientTo: "#3b82f6",
        Icon: Briefcase,
    },
    resume: {
        chipLabel: "Resume",
        title: "Upload Resume",
        dropPrompt: "Drop your resume here",
        successLabel: "resume",
        accentBg: "#f0fdf4",
        accentColor: "#16a34a",
        iconBg: "#bbf7d0",
        gradientFrom: "#16a34a",
        gradientTo: "#22c55e",
        Icon: FileUser,
    },
    logo: {
        chipLabel: "Logo",
        title: "Upload Company Logo",
        dropPrompt: "Drop your company logo here",
        successLabel: "logo",
        accentBg: "#fef3c7",
        accentColor: "#d97706",
        iconBg: "#fef08a",
        gradientFrom: "#d97706",
        gradientTo: "#f59e0b",
        Icon: Briefcase,
    },
};

const getConfig = (uploadType) => TYPE_CONFIG[uploadType] || TYPE_CONFIG.jd;

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────
const getExt = (name) => (name.split(".").pop() || "").toLowerCase();

const getFileColor = (name) => {
    const ext = getExt(name);
    if (ext === "pdf") return { bg: "#fee2e2", color: "#dc2626" };
    if (ext === "doc" || ext === "docx") return { bg: "#dbeafe", color: "#2563eb" };
    return { bg: "#f1f5f9", color: "#64748b" };
};

const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

// ─────────────────────────────────────────────
// Animations
// ─────────────────────────────────────────────
const slideIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0);   }
`;
const fadeScale = keyframes`
  from { opacity: 0; transform: scale(0.92); }
  to   { opacity: 1; transform: scale(1);    }
`;
const spin = keyframes`
  to { transform: rotate(360deg); }
`;
const successPop = keyframes`
  0%   { transform: scale(0.5); opacity: 0; }
  65%  { transform: scale(1.15); opacity: 1; }
  100% { transform: scale(1); }
`;
// const shimmer = keyframes`
//   0%   { background-position: -400px 0; }
//   100% { background-position: 400px 0; }
// `;

// ─────────────────────────────────────────────
// Styled Components
// ─────────────────────────────────────────────
const DropZone = styled(Box)(({ isDragOver, accentcolor, iconbg }) => ({
    padding: "32px 24px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    border: `1.5px dashed ${isDragOver ? accentcolor : "#e2ddd8"}`,
    borderRadius: "14px",
    cursor: "pointer",
    background: isDragOver ? `${iconbg}55` : "#faf9f7",
    transform: isDragOver ? "scale(1.01)" : "scale(1)",
    transition: "all 0.22s ease",
    position: "relative",
    overflow: "hidden",
    "&:hover": {
        borderColor: accentcolor,
        background: `${iconbg}33`,
        "& .drop-icon-wrap": { background: iconbg },
        "& .drop-main-icon": { color: accentcolor, transform: "translateY(-2px)" },
    },
    // subtle noise texture overlay
    "&::before": {
        content: '""',
        position: "absolute",
        inset: 0,
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.025'/%3E%3C/svg%3E")`,
        pointerEvents: "none",
        borderRadius: "14px",
    },
}));

const DropIconWrap = styled(Box)(({ iconbg }) => ({
    className: "drop-icon-wrap",
    width: 56,
    height: 56,
    borderRadius: "14px",
    background: iconbg || "#f0ede8",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "14px",
    transition: "background 0.2s",
    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
}));

const FileItemRow = styled(Box)({
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "10px 12px",
    border: "1px solid #f0ede8",
    borderRadius: "11px",
    marginBottom: "6px",
    background: "#fff",
    animation: `${slideIn} 0.2s ease`,
    transition: "border-color 0.15s, background 0.15s, box-shadow 0.15s",
    "&:hover": {
        borderColor: "#d1d5db",
        background: "#faf9f7",
        boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
    },
});

const FileIconBox = styled(Box)(({ bgcolor }) => ({
    width: 36,
    height: 36,
    borderRadius: "9px",
    background: bgcolor,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
}));

const RemoveBtn = styled(IconButton)({
    width: 26,
    height: 26,
    borderRadius: "50%",
    padding: 0,
    color: "#9ca3af",
    transition: "all 0.15s",
    "&:hover": { background: "#fee2e2", color: "#dc2626" },
});

const SpinnerBox = styled(Box)(({ accentcolor }) => ({
    width: 14,
    height: 14,
    border: `2px solid ${accentcolor}30`,
    borderTopColor: accentcolor || "#2563eb",
    borderRadius: "50%",
    animation: `${spin} 0.7s linear infinite`,
    display: "inline-block",
    flexShrink: 0,
}));

const SuccessIconWrap = styled(Box)(({ accentbg }) => ({
    width: 64,
    height: 64,
    borderRadius: "50%",
    background: accentbg || "#f0fdf4",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 14px",
    animation: `${successPop} 0.45s cubic-bezier(0.34,1.56,0.64,1) forwards`,
    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
}));

const ErrorBanner = styled(Box)({
    display: "flex",
    alignItems: "flex-start",
    gap: "8px",
    padding: "10px 12px",
    background: "#fff7ed",
    border: "1px solid #fed7aa",
    borderRadius: "10px",
    marginTop: "12px",
    animation: `${slideIn} 0.2s ease`,
});

// ─────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────
const FileUploadModal = ({
    open,
    onClose,
    uploadType = "jd",
    apiEndpoint,
    extraPayload = {},
    onSuccess,
    onError,
    setLoading,
    postUploadHandler,
}) => {
    const cfg = getConfig(uploadType);

    const [files, setFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [uploadDone, setUploadDone] = useState(false);
    const [progress, setProgress] = useState(0);
    const [quote, setQuote] = useState("");
    const [dragOver, setDragOver] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");
    const fileInputRef = useRef(null);
    const errorTimerRef = useRef(null);

    const showError = useCallback((msg) => {
        setErrorMsg(msg);
        clearTimeout(errorTimerRef.current);
        errorTimerRef.current = setTimeout(() => setErrorMsg(""), 5000);
    }, []);

    const addFiles = useCallback((newFiles) => {
        const maxSize = getMaxFileSize(uploadType);
        const allowedExts = getAllowedExts(uploadType);
        const formatLabels = getFormatLabels(uploadType);

        let hasLarge = false;
        const valid = [];
        newFiles.forEach((f) => {
            const ext = getExt(f.name);
            if (!allowedExts.includes(ext)) {
                const supportedFormats = formatLabels.join(", ");
                showError(`"${f.name}" is not supported. Use ${supportedFormats}.`);
                return;
            }
            if (f.size > maxSize * 1024 * 1024) { hasLarge = true; return; }
            if (files.find((x) => x.name === f.name && x.size === f.size)) {
                showError(`"${f.name}" is already added.`);
                return;
            }
            valid.push(f);
        });
        if (hasLarge) {
            showError(`Files above ${maxSize} MB were skipped.`);
            onError?.({ message: `File is above ${maxSize}MB. Please upload a file below ${maxSize} MB.`, severity: "warning", isRawMessage: true });
        }
        if (valid.length > 0) setFiles((prev) => [...prev, ...valid]);
    }, [files, showError, onError, uploadType]);

    const handleFileChange = (e) => {
        if (e.target.files?.length) addFiles(Array.from(e.target.files));
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const removeFile = (index) => setFiles((prev) => prev.filter((_, i) => i !== index));

    const handleDragOver = (e) => { e.preventDefault(); setDragOver(true); };
    const handleDragLeave = () => setDragOver(false);
    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        addFiles(Array.from(e.dataTransfer.files || []));
    };

    const handleUpload = async () => {
        if (!files.length || uploading) return;
        setQuote(QUOTES[Math.floor(Math.random() * QUOTES.length)]);
        setUploading(true);
        setLoading?.(true);
        setProgress(0);

        let pct = 0;
        const iv = setInterval(() => {
            pct += Math.random() * 18;
            if (pct >= 92) pct = 92;
            setProgress(Math.round(pct));
        }, 300);

        const formData = new FormData();
        // Use appropriate field name based on upload type
        const fieldName = uploadType === "logo" ? "logo" : "files";
        files.forEach((file) => formData.append(fieldName, file));
        formData.append("type", uploadType);
        Object.keys(extraPayload).forEach((key) => formData.append(key, extraPayload[key]));
        // console.log("form Data is ", formData)

        try {
            const response = await apiEndpoint(formData);
            const { status, message, data } = response || {};

            if (status === 400) { onError?.({ message: message || "Invalid file upload request.", severity: "warning" }); return; }
            if (status === 403) { onError?.({ message, severity: "error", planExhaust: true }); return; }
            if (status >= 500) { onError?.({ message: message || "Server error while uploading files.", severity: "error" }); return; }

            if ([200, 201, 202].includes(status)) {
                clearInterval(iv);
                setProgress(100);
                if (postUploadHandler) await postUploadHandler(data);
                await new Promise((r) => setTimeout(r, 350));
                setUploadDone(true);
                onSuccess?.({ message: message || "Files uploaded successfully!", data });
                return;
            }
            onError?.({ message: message || "Something went wrong", severity: "error" });
        } catch (error) {
            const status = error?.response?.status;
            const backendMessage = error?.response?.data?.message;
            if (status === 401) { onError?.({ message: "Session expired. Please login again.", severity: "error" }); return; }
            onError?.({ message: backendMessage || "Unexpected error occurred", severity: "error" });
        } finally {
            clearInterval(iv);
            setUploading(false);
            setLoading?.(false);
            if (!uploadDone) onClose();
        }
    };

    const handleClose = () => {
        setFiles([]);
        setUploading(false);
        setUploadDone(false);
        setProgress(0);
        setErrorMsg("");
        onClose();
    };

    const TypeIcon = cfg.Icon;

    return (
        <Dialog
            open={open}
            onClose={!uploading ? handleClose : undefined}
            fullWidth
            maxWidth="xs"
            PaperProps={{
                sx: {
                    width: 440,
                    borderRadius: "20px",
                    overflow: "hidden",
                    border: "1px solid #e9e6e1",
                    boxShadow: "0 32px 64px rgba(0,0,0,0.13), 0 8px 24px rgba(0,0,0,0.06)",
                    fontFamily: "'DM Sans', system-ui, sans-serif",
                    animation: `${fadeScale} 0.25s ease`,
                },
            }}
        >
            {/* ── Decorative top stripe ── */}
            <Box sx={{
                height: 4,
                background: `linear-gradient(90deg, ${cfg.gradientFrom}, ${cfg.gradientTo})`,
            }} />

            {/* ── Header ── */}
            <DialogTitle sx={{ px: 3, pt: 2.5, pb: 0, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <Box>
                    {/* Dynamic chip based on uploadType */}
                    <Chip
                        icon={<TypeIcon size={12} />}
                        label={cfg.chipLabel}
                        size="small"
                        sx={{
                            fontSize: "11px",
                            fontWeight: 600,
                            letterSpacing: "0.04em",
                            textTransform: "uppercase",
                            background: cfg.accentBg,
                            color: cfg.accentColor,
                            border: `1px solid ${cfg.iconBg}`,
                            height: 24,
                            mb: 1,
                            "& .MuiChip-icon": { color: cfg.accentColor, ml: "8px" },
                        }}
                    />
                    {/* Dynamic title based on uploadType */}
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 700,
                            color: "#0f0e0d",
                            letterSpacing: "-0.025em",
                            lineHeight: 1.2,
                            fontSize: "1.05rem",
                        }}
                    >
                        {cfg.title}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#9ca3af", mt: "3px", display: "block", fontSize: "0.72rem" }}>
                        Accepted formats: {getFormatLabels(uploadType).join(", ")} · Max {getMaxFileSize(uploadType)} MB
                    </Typography>
                </Box>
                <IconButton
                    onClick={handleClose}
                    disabled={uploading}
                    size="small"
                    sx={{
                        width: 32, height: 32,
                        border: "1px solid #e9e6e1",
                        borderRadius: "50%",
                        color: "#6b7280",
                        mt: 0.4,
                        "&:hover": { background: "#f5f3f0", color: "#0f0e0d" },
                    }}
                >
                    <X size={14} />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers={false} sx={{ px: 3, pt: 2.5, pb: 1 }}>

                {/* ── Success State ── */}
                {uploadDone ? (
                    <Box sx={{ textAlign: "center", py: 2, animation: `${slideIn} 0.3s ease` }}>
                        <SuccessIconWrap accentbg={cfg.accentBg}>
                            <CheckCircle size={30} color={cfg.accentColor} />
                        </SuccessIconWrap>
                        <Typography sx={{ fontWeight: 700, color: "#0f0e0d", fontSize: "1rem", mb: "6px" }}>
                            Upload successful!
                        </Typography>
                        {/* Dynamic success message based on uploadType */}
                        <Typography sx={{ fontSize: "0.82rem", color: "#6b7280", lineHeight: 1.5 }}>
                            {files.length} {cfg.successLabel} file{files.length !== 1 ? "s" : ""} uploaded successfully.
                        </Typography>
                    </Box>
                ) : (
                    <>
                        {/* ── Drop Zone ── */}
                        {!uploading && (
                            <DropZone
                                isDragOver={dragOver}
                                accentcolor={cfg.accentColor}
                                iconbg={cfg.iconBg}
                                onClick={() => fileInputRef.current?.click()}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop}
                            >
                                <DropIconWrap iconbg={cfg.iconBg} className="drop-icon-wrap">
                                    <CloudUpload
                                        size={24}
                                        className="drop-main-icon"
                                        color={cfg.accentColor}
                                        style={{ transition: "transform 0.2s, color 0.2s" }}
                                    />
                                </DropIconWrap>

                                {/* Dynamic drop prompt based on uploadType */}
                                <Typography sx={{ fontSize: "0.88rem", fontWeight: 600, color: "#1f2937", mb: "4px", textAlign: "center" }}>
                                    {cfg.dropPrompt}{" "}
                                    <Box component="span" sx={{ color: cfg.accentColor }}>browse</Box>
                                </Typography>
                                <Typography sx={{ fontSize: "0.73rem", color: "#9ca3af", mb: 1.5 }}>
                                    Drag & drop or click to select
                                </Typography>

                                <Box sx={{ display: "flex", gap: "6px", flexWrap: "wrap", justifyContent: "center" }}>
                                    {getFormatLabels(uploadType).map((fmt) => (
                                        <Box
                                            key={fmt}
                                            sx={{
                                                fontSize: "11px",
                                                fontWeight: 600,
                                                color: "#4b5563",
                                                background: "#fff",
                                                border: "1px solid #e9e6e1",
                                                px: "9px",
                                                py: "3px",
                                                borderRadius: "7px",
                                                letterSpacing: "0.03em",
                                                boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                                            }}
                                        >
                                            {fmt}
                                        </Box>
                                    ))}
                                </Box>

                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    hidden
                                    multiple
                                    accept={getAcceptedTypes(uploadType)}
                                    onChange={handleFileChange}
                                />
                            </DropZone>
                        )}

                        {/* ── Error Banner ── */}
                        {errorMsg && (
                            <ErrorBanner>
                                <AlertTriangle size={15} color="#d97706" style={{ flexShrink: 0, marginTop: 1 }} />
                                <Typography sx={{ fontSize: "0.75rem", color: "#92400e", lineHeight: 1.4 }}>{errorMsg}</Typography>
                            </ErrorBanner>
                        )}

                        {/* ── File List ── */}
                        {!uploading && files.length > 0 && (
                            <Box sx={{ mt: 2 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: "6px", mb: 1 }}>
                                    <Typography sx={{ fontSize: "0.7rem", fontWeight: 600, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                                        Selected
                                    </Typography>
                                    <Box sx={{
                                        background: "#1f2937",
                                        color: "#fff",
                                        fontSize: "10px",
                                        fontWeight: 600,
                                        px: "7px",
                                        py: "2px",
                                        borderRadius: "10px",
                                    }}>
                                        {files.length}
                                    </Box>
                                </Box>
                                <List dense disablePadding sx={{ maxHeight: 180, overflowY: "auto", pr: 0.5 }}>
                                    {files.map((file, index) => {
                                        const { bg, color } = getFileColor(file.name);
                                        return (
                                            <FileItemRow key={`${file.name}-${index}`} component="li">
                                                <FileIconBox bgcolor={bg}>
                                                    <FileText size={15} color={color} />
                                                </FileIconBox>
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Typography sx={{ fontSize: "0.8rem", fontWeight: 500, color: "#1f2937", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                        {file.name}
                                                    </Typography>
                                                    <Typography sx={{ fontSize: "0.68rem", color: "#9ca3af", mt: "1px" }}>
                                                        {formatSize(file.size)}
                                                    </Typography>
                                                </Box>
                                                <RemoveBtn onClick={() => removeFile(index)} size="small">
                                                    <X size={12} />
                                                </RemoveBtn>
                                            </FileItemRow>
                                        );
                                    })}
                                </List>
                            </Box>
                        )}

                        {/* ── Progress ── */}
                        {uploading && (
                            <Box sx={{ mt: 1, animation: `${slideIn} 0.25s ease` }}>
                                {/* Uploading file names preview */}
                                {files.length > 0 && (
                                    <Box sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        mb: 2,
                                        p: 1.5,
                                        bgcolor: "#f9fafb",
                                        borderRadius: "10px",
                                        border: "1px solid #f0ede8",
                                    }}>
                                        <Box sx={{ width: 32, height: 32, borderRadius: "8px", bgcolor: cfg.accentBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                            <FileText size={15} color={cfg.accentColor} />
                                        </Box>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography sx={{ fontSize: "0.78rem", fontWeight: 500, color: "#1f2937", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                {files.length === 1 ? files[0].name : `${files.length} files selected`}
                                            </Typography>
                                            <Typography sx={{ fontSize: "0.68rem", color: "#9ca3af" }}>
                                                {files.reduce((sum, f) => sum + f.size, 0) > 0 ? formatSize(files.reduce((sum, f) => sum + f.size, 0)) : ""}
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}

                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: "8px" }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                        <SpinnerBox accentcolor={cfg.accentColor} />
                                        <Typography sx={{ fontSize: "0.8rem", fontWeight: 500, color: "#1f2937" }}>
                                            Uploading…
                                        </Typography>
                                    </Box>
                                    <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: cfg.accentColor }}>
                                        {progress}%
                                    </Typography>
                                </Box>

                                <LinearProgress
                                    variant="determinate"
                                    value={progress}
                                    sx={{
                                        height: 7,
                                        borderRadius: 6,
                                        background: "#f0ede8",
                                        "& .MuiLinearProgress-bar": {
                                            background: `linear-gradient(90deg, ${cfg.gradientFrom}, ${cfg.gradientTo})`,
                                            borderRadius: 6,
                                            transition: "transform 0.3s ease",
                                        },
                                    }}
                                />
                                <Typography sx={{ fontSize: "0.72rem", color: "#9ca3af", mt: "7px", fontStyle: "italic", textAlign: "center" }}>
                                    {quote}
                                </Typography>
                            </Box>
                        )}
                    </>
                )}
            </DialogContent>

            {/* ── Footer ── */}
            <DialogActions sx={{ px: 3, py: 2.5, justifyContent: "space-between", borderTop: "1px solid #f0ede8" }}>
                {uploadDone ? (
                    <>
                        <span />
                        <CustomButton
                            variant="contained"
                            onClick={handleClose}
                            startIcon={<CheckCircle size={15} />}
                            sx={{
                                background: `linear-gradient(135deg, ${cfg.gradientFrom}, ${cfg.gradientTo})`,
                                color: "#fff",
                                borderRadius: "10px",
                                textTransform: "none",
                                fontWeight: 600,
                                px: 2.5,
                                boxShadow: "none",
                                "&:hover": { opacity: 0.9, boxShadow: "none" },
                            }}
                        >
                            Done
                        </CustomButton>
                    </>
                ) : (
                    <>
                        <CustomButton
                            onClick={handleClose}
                            disabled={uploading}
                            startIcon={<ArrowLeft size={14} />}
                            sx={{
                                borderRadius: "10px",
                                textTransform: "none",
                                color: "#6b7280",
                                border: "1px solid #e9e6e1",
                                fontWeight: 500,
                                fontSize: "0.82rem",
                                "&:hover": { background: "#f5f3f0", borderColor: "#d1d5db" },
                            }}
                        >
                            Cancel
                        </CustomButton>
                        <CustomButton
                            onClick={handleUpload}
                            variant="contained"
                            disabled={files.length === 0 || uploading}
                            startIcon={uploading ? null : <Upload size={14} />}
                            sx={{
                                background: files.length === 0
                                    ? "#e5e7eb"
                                    : `linear-gradient(135deg, ${cfg.gradientFrom}, ${cfg.gradientTo})`,
                                color: files.length === 0 ? "#9ca3af" : "#fff",
                                borderRadius: "10px",
                                textTransform: "none",
                                fontWeight: 600,
                                fontSize: "0.82rem",
                                px: 2.5,
                                boxShadow: files.length > 0 ? `0 4px 14px ${cfg.accentColor}35` : "none",
                                transition: "all 0.2s ease",
                                "&:hover": {
                                    opacity: 0.92,
                                    boxShadow: files.length > 0 ? `0 6px 18px ${cfg.accentColor}45` : "none",
                                },
                                "&:disabled": { background: "#e5e7eb", color: "#9ca3af", boxShadow: "none" },
                            }}
                        >
                            {files.length > 0
                                ? `Upload ${files.length} file${files.length > 1 ? "s" : ""}`
                                : "Upload"}
                        </CustomButton>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default FileUploadModal;